import { useMutation } from "@tanstack/react-query";
import { App, Form, Input, Modal, Select } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { useServerNotifications } from "../api/notifications";
import type { NotificationsResponse } from "../api/types";

interface WeekTimeValues {
  day: number | null;
  time: string;
  additional_notes: string;
}

/** What the dialog needs of a week; both screens' rows satisfy it. */
export interface EditableWeek {
  week_begin: string;
  week_label: string;
  /** 0 = Sunday. Null means the congregation's usual day stands. */
  week_day?: number | null;
  day_time?: string | null;
  additional_notes?: string | null;
  /** Only used in the title, where it says whose week is being moved. */
  speaker_name?: string | null;
}

/**
 * Day and time of a single week, plus its note.
 *
 * The congregation's usual meeting time comes from
 * /congregation/congregation_times; what is set here overrides it for this week
 * alone. The visiting speaker is told about the change — the server sends that
 * mail — which is why this sits on the screen that books them.
 *
 * The note travels along because the endpoint writes it unconditionally; left
 * out, it would be cleared.
 */
export function WeekTimeDialog({
  week,
  onClose,
  onChanged,
}: {
  week: EditableWeek;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const [form] = Form.useForm<WeekTimeValues>();

  const weekDays = useMemo(
    () =>
      (t("date.day_names", { returnObjects: true }) as string[]).map((label, index) => ({
        value: index,
        label,
      })),
    [t],
  );

  const save = useMutation({
    mutationFn: (values: WeekTimeValues) =>
      apiFetch<NotificationsResponse>(`/local_assignation/events/${week.week_begin}`, {
        method: "PUT",
        body: values,
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      onChanged();
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  return (
    <Modal
      open
      title={[week.week_label, week.speaker_name].filter(Boolean).join(" · ")}
      onCancel={onClose}
      okText={t("form.update")}
      okButtonProps={{ loading: save.isPending }}
      onOk={() => form.submit()}
    >
      <Form<WeekTimeValues>
        form={form}
        layout="vertical"
        initialValues={{
          day: week.week_day ?? null,
          time: week.day_time ?? "",
          additional_notes: week.additional_notes ?? "",
        }}
        onFinish={(values) => save.mutate(values)}
      >
        <Form.Item name="day" label={t("activerecord.attributes.local_assignation.week_day")}>
          <Select options={weekDays} allowClear style={{ width: 200 }} />
        </Form.Item>

        <Form.Item
          name="time"
          label={t("activerecord.attributes.local_assignation.day_time")}
          rules={[
            {
              pattern: /^([01]\d|2[0-3]):[0-5]\d$/,
              message: t(
                "activerecord.errors.models.congregation_meeting_time.attributes.day_time.invalid_format",
              ),
            },
          ]}
        >
          <Input placeholder="hh:mm" style={{ width: 140 }} />
        </Form.Item>

        <Form.Item
          name="additional_notes"
          label={t("activerecord.attributes.local_assignation.additional_notes")}
        >
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
